/**
 * CDD Questionnaire Config Panel
 *
 * Displayed when a CDD Questionnaire node is selected in the workflow builder.
 * Allows selecting a framework template and editing/adding questions with risk scoring.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react';
import { useAuth } from '../../auth';

const FRAMEWORKS = [
  { value: 'cdd_fincen', label: 'FinCEN (US Financial)' },
  { value: 'cdd_crypto', label: 'Crypto / Digital Assets' },
  { value: 'cdd_banking', label: 'Banking (Traditional)' },
  { value: 'cdd_fintech', label: 'FinTech' },
  { value: 'custom', label: 'Custom (No Template)' },
];

const QUESTION_TYPES = [
  { value: 'select', label: 'Dropdown' },
  { value: 'multi_select', label: 'Multi-Select' },
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
];

const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, '') || 'https://getclearance-production.up.railway.app';

export default function CddQuestionnaireConfig({ node, onUpdate }) {
  const { getToken } = useAuth();
  const config = node.data.config || {};
  const [framework, setFramework] = useState(config.framework || 'cdd_fincen');
  const [questions, setQuestions] = useState(config.questions || []);
  const [additionalQuestions, setAdditionalQuestions] = useState(config.additional_questions || []);
  const [riskScoringEnabled, setRiskScoringEnabled] = useState(config.risk_scoring_enabled !== false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [templates, setTemplates] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const token = await getToken();
      const resp = await fetch(`${API_BASE}/api/v1/workflow-builder/cdd-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setTemplates(data.templates);
        return data.templates;
      }
    } catch (e) {
      console.error('Failed to fetch CDD templates:', e);
    }
    return null;
  }, [getToken]);

  // Fetch templates on mount and load initial framework
  useEffect(() => {
    const init = async () => {
      const loadedTemplates = await fetchTemplates();
      // Load initial template if no questions stored yet
      if (loadedTemplates && !config.questions?.length && framework !== 'custom') {
        const template = loadedTemplates[framework];
        if (template) {
          setQuestions(template.questions || []);
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConfig = (fw, qs, addQs, riskEnabled) => {
    onUpdate({
      ...node,
      data: {
        ...node.data,
        config: {
          ...node.data.config,
          framework: fw,
          questions: qs,
          additional_questions: addQs,
          risk_scoring_enabled: riskEnabled,
        },
      },
    });
  };

  const handleFrameworkChange = (e) => {
    const newFramework = e.target.value;
    setFramework(newFramework);
    if (newFramework === 'custom') {
      setQuestions([]);
      updateConfig(newFramework, [], additionalQuestions, riskScoringEnabled);
    } else if (templates && templates[newFramework]) {
      const templateQuestions = templates[newFramework].questions || [];
      setQuestions(templateQuestions);
      updateConfig(newFramework, templateQuestions, additionalQuestions, riskScoringEnabled);
    }
  };

  const handleRiskToggle = (e) => {
    const enabled = e.target.checked;
    setRiskScoringEnabled(enabled);
    updateConfig(framework, questions, additionalQuestions, enabled);
  };

  const addQuestion = () => {
    const newQ = {
      id: `custom_${Date.now()}`,
      type: 'select',
      label: '',
      helpText: '',
      required: true,
      options: [{ value: '', label: '', riskScore: 0 }],
      order: (additionalQuestions.length || 0) + questions.length + 1,
    };
    setEditingQuestion(newQ);
  };

  const saveQuestion = (q) => {
    // Check if this is a template question being edited
    if (q._isTemplate) {
      const { _isTemplate, ...cleanQ } = q;
      const updatedQuestions = questions.map((tq) => tq.id === cleanQ.id ? cleanQ : tq);
      setQuestions(updatedQuestions);
      updateConfig(framework, updatedQuestions, additionalQuestions, riskScoringEnabled);
      setEditingQuestion(null);
      return;
    }
    const updated = [...additionalQuestions];
    const existingIdx = updated.findIndex((aq) => aq.id === q.id);
    if (existingIdx >= 0) {
      updated[existingIdx] = q;
    } else {
      updated.push(q);
    }
    setAdditionalQuestions(updated);
    updateConfig(framework, questions, updated, riskScoringEnabled);
    setEditingQuestion(null);
  };

  const removeAdditionalQuestion = (id) => {
    const updated = additionalQuestions.filter((q) => q.id !== id);
    setAdditionalQuestions(updated);
    updateConfig(framework, questions, updated, riskScoringEnabled);
  };

  const moveQuestion = (idx, direction) => {
    const updated = [...additionalQuestions];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= updated.length) return;
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setAdditionalQuestions(updated);
    updateConfig(framework, questions, updated, riskScoringEnabled);
  };

  const allQuestions = [...questions, ...additionalQuestions];

  return (
    <div className="cdd-config">
      <div className="form-group">
        <label>Framework Template</label>
        <select value={framework} onChange={handleFrameworkChange} className="config-select">
          {FRAMEWORKS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="checkbox">
          <input type="checkbox" checked={riskScoringEnabled} onChange={handleRiskToggle} />
          <span>Enable per-answer risk scoring</span>
        </label>
      </div>

      <div className="form-group">
        <label>Questions ({allQuestions.length})</label>
        <div className="question-list">
          {questions.map((q, i) => (
            <QuestionItem
              key={q.id}
              question={q}
              index={i}
              isTemplate={true}
              riskScoringEnabled={riskScoringEnabled}
              onEdit={() => setEditingQuestion({ ...q, _isTemplate: true })}
            />
          ))}
          {additionalQuestions.map((q, i) => (
            <QuestionItem
              key={q.id}
              question={q}
              index={questions.length + i}
              isTemplate={false}
              riskScoringEnabled={riskScoringEnabled}
              onEdit={() => setEditingQuestion(q)}
              onRemove={() => removeAdditionalQuestion(q.id)}
              onMoveUp={i > 0 ? () => moveQuestion(i, -1) : null}
              onMoveDown={i < additionalQuestions.length - 1 ? () => moveQuestion(i, 1) : null}
            />
          ))}
        </div>
        <button className="add-question-btn" onClick={addQuestion}>
          <Plus size={14} />
          Add Custom Question
        </button>
      </div>

      {editingQuestion && (
        <QuestionEditor
          question={editingQuestion}
          riskScoringEnabled={riskScoringEnabled}
          onSave={saveQuestion}
          onCancel={() => setEditingQuestion(null)}
        />
      )}

      <style>{`
        .cdd-config {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .config-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 13px;
        }
        .question-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: 8px;
        }
        .question-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }
        .question-item:hover {
          border-color: var(--accent-primary);
        }
        .question-item .q-info {
          flex: 1;
          min-width: 0;
        }
        .question-item .q-label {
          font-weight: 500;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .question-item .q-meta {
          display: flex;
          gap: 6px;
          margin-top: 2px;
        }
        .question-item .q-badge {
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 3px;
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }
        .question-item .q-badge.risk {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        .question-item .q-badge.template {
          background: var(--accent-glow-medium);
          color: var(--accent-primary);
        }
        .question-item .q-actions {
          display: flex;
          gap: 2px;
        }
        .question-item .q-actions button {
          padding: 2px;
          border: none;
          background: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-radius: 3px;
        }
        .question-item .q-actions button:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
        }
        .add-question-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px dashed var(--border-color);
          border-radius: 6px;
          background: none;
          color: var(--text-secondary);
          font-size: 12px;
          cursor: pointer;
          width: 100%;
          justify-content: center;
        }
        .add-question-btn:hover {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
        }
        .question-editor-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .question-editor {
          background: var(--bg-primary);
          border-radius: 12px;
          padding: 20px;
          width: 480px;
          max-height: 80vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
        }
        .question-editor h4 {
          margin: 0 0 16px;
          font-size: 15px;
          font-weight: 600;
        }
        .question-editor .form-group {
          margin-bottom: 12px;
        }
        .question-editor .form-group label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }
        .question-editor .form-group input,
        .question-editor .form-group select,
        .question-editor .form-group textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 13px;
        }
        .question-editor .option-row input {
          width: auto;
        }
        .option-row {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 6px;
        }
        .option-row input[type="text"] {
          flex: 1 1 auto;
          width: auto;
          min-width: 0;
        }
        .option-row input[type="number"] {
          width: 60px;
          flex: 0 0 60px;
        }
        .option-row button {
          padding: 4px;
          border: none;
          background: none;
          color: var(--text-secondary);
          cursor: pointer;
          flex: 0 0 auto;
        }
        .option-row button:hover {
          color: #ef4444;
        }
        .editor-actions {
          display: flex;
          gap: 8px;
          margin-top: 16px;
          justify-content: flex-end;
        }
        .editor-actions button {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border: none;
        }
        .editor-actions .save-btn {
          background: var(--accent-primary);
          color: white;
        }
        .editor-actions .cancel-btn {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
        }
      `}</style>
    </div>
  );
}

function QuestionItem({ question, index, isTemplate, riskScoringEnabled, onEdit, onRemove, onMoveUp, onMoveDown }) {
  const maxRisk = riskScoringEnabled && question.options
    ? Math.max(...question.options.map((o) => o.riskScore || 0))
    : 0;

  return (
    <div className="question-item" onClick={onEdit}>
      <div className="q-info">
        <div className="q-label">{question.label || '(untitled)'}</div>
        <div className="q-meta">
          <span className="q-badge">{question.type}</span>
          {isTemplate && <span className="q-badge template">template</span>}
          {riskScoringEnabled && maxRisk > 0 && (
            <span className="q-badge risk">max risk: {maxRisk}</span>
          )}
          {question.required && <span className="q-badge">required</span>}
        </div>
      </div>
      {!isTemplate && (
        <div className="q-actions" onClick={(e) => e.stopPropagation()}>
          {onMoveUp && (
            <button onClick={onMoveUp}><ChevronUp size={12} /></button>
          )}
          {onMoveDown && (
            <button onClick={onMoveDown}><ChevronDown size={12} /></button>
          )}
          {onRemove && (
            <button onClick={onRemove}><Trash2 size={12} /></button>
          )}
        </div>
      )}
    </div>
  );
}

function QuestionEditor({ question, riskScoringEnabled, onSave, onCancel }) {
  const [q, setQ] = useState({ ...question });
  const needsOptions = ['select', 'multi_select'].includes(q.type);
  const isNew = q.id.startsWith('custom_') && !question._isTemplate;

  const updateField = (field, value) => setQ({ ...q, [field]: value });

  const updateOption = (idx, field, value) => {
    const options = [...(q.options || [])];
    options[idx] = { ...options[idx], [field]: value };
    setQ({ ...q, options });
  };

  const addOption = () => {
    const options = [...(q.options || []), { value: '', label: '', riskScore: 0 }];
    setQ({ ...q, options });
  };

  const removeOption = (idx) => {
    const options = (q.options || []).filter((_, i) => i !== idx);
    setQ({ ...q, options });
  };

  const handleSave = () => {
    // Auto-generate value from label if not set
    const options = (q.options || []).map((o) => ({
      ...o,
      value: o.value || o.label.toLowerCase().replace(/\s+/g, '_'),
    }));
    onSave({ ...q, options });
  };

  return (
    <div className="question-editor-overlay" onClick={onCancel}>
      <div className="question-editor" onClick={(e) => e.stopPropagation()}>
        <h4>{isNew ? 'Add Question' : 'Edit Question'}</h4>

        <div className="form-group">
          <label>Question Text</label>
          <input
            type="text"
            value={q.label}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="e.g., What is your primary source of funds?"
          />
        </div>

        <div className="form-group">
          <label>Help Text</label>
          <input
            type="text"
            value={q.helpText || ''}
            onChange={(e) => updateField('helpText', e.target.value)}
            placeholder="Additional guidance for the applicant"
          />
        </div>

        <div className="form-group">
          <label>Type</label>
          <select value={q.type} onChange={(e) => updateField('type', e.target.value)}>
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="checkbox">
            <input type="checkbox" checked={q.required !== false} onChange={(e) => updateField('required', e.target.checked)} />
            <span>Required</span>
          </label>
        </div>

        {needsOptions && (
          <div className="form-group">
            <label>Options {riskScoringEnabled && '(with risk scores)'}</label>
            {(q.options || []).map((opt, i) => (
              <div key={i} className="option-row">
                <input
                  type="text"
                  style={{ flex: '1 1 auto', width: 'auto', minWidth: 0 }}
                  value={opt.label || opt.value || ''}
                  onChange={(e) => {
                    updateOption(i, 'label', e.target.value);
                    updateOption(i, 'value', e.target.value.toLowerCase().replace(/\s+/g, '_'));
                  }}
                  placeholder="Option label"
                />
                {riskScoringEnabled && (
                  <input
                    type="number"
                    style={{ width: '60px', flex: '0 0 60px' }}
                    value={opt.riskScore || 0}
                    onChange={(e) => updateOption(i, 'riskScore', parseInt(e.target.value) || 0)}
                    min={0}
                    max={100}
                    title="Risk score"
                  />
                )}
                <button onClick={() => removeOption(i)}><X size={14} /></button>
              </div>
            ))}
            <button className="add-question-btn" onClick={addOption} style={{ marginTop: 4 }}>
              <Plus size={12} /> Add Option
            </button>
          </div>
        )}

        <div className="editor-actions">
          <button className="cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="save-btn" onClick={handleSave} disabled={!q.label}>
            Save Question
          </button>
        </div>
      </div>
    </div>
  );
}
